import type {
  LockerverseAuctionItem as AuctionItem,
  LockerverseAuction,
} from "@lockerverse/sdk/auctions";
import { Elements } from "@stripe/react-stripe-js";
import type {
  StripeElementsOptions,
  StripeElementsOptionsMode,
} from "@stripe/stripe-js";
import {
  ArrowRight,
  ChevronLeft,
  Gavel,
  ShieldCheck,
  TimerReset,
} from "lucide-react";
import {
  type RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { AuctionCheckout } from "./auction-checkout.tsx";
import { getAuctionDisplayStatus } from "./auction-display.ts";
import { AuctionItemDetails } from "./auction-item-details.tsx";
import { AuctionItemSelection } from "./auction-item-selection.tsx";
import type { LockerverseAuctionItemProps } from "./auction-item-types.ts";
import {
  type AuctionSelection,
  getAuctionSelectionPricing,
} from "./auction-payment.ts";
import { notifyHostCallback } from "./host-callback.ts";
import { useStripeAppearance } from "./stripe-appearance.ts";
import { defaultLockerverseStripeLoader } from "./stripe-loader.ts";
import { useAuctionBoundary } from "./use-auction-boundary.ts";

function AuctionPayment(
  props: LockerverseAuctionItemProps & {
    appearanceRoot: RefObject<HTMLElement | null>;
    interactionLocked: boolean;
    item: AuctionItem;
    selection: AuctionSelection;
    onReview: (auction: LockerverseAuction, message: string) => void;
    onBack: () => void;
    contact: { email: string; phone: string };
    onContactChange: (contact: { email: string; phone: string }) => void;
    onIncludeServiceFeeChange: (value: boolean) => void;
    onInteractionLockChange: (locked: boolean) => void;
  }
) {
  const {
    auction,
    item,
    selection,
    theme = "dark",
    stripeLoader = defaultLockerverseStripeLoader,
    style,
    onBack,
    resumePurchase,
  } = props;
  const [stripe, setStripe] =
    useState<Awaited<ReturnType<typeof defaultLockerverseStripeLoader>>>(null);
  const [failed, setFailed] = useState(false);
  const [revision, setRevision] = useState(0);
  const appearance = useStripeAppearance(
    props.appearanceRoot,
    theme,
    style,
    props.interactionLocked
  );
  const payment = resumePurchase?.payment ?? auction.payment;
  useEffect(() => {
    let active = true;
    if (!payment) {
      setFailed(true);
      return;
    }
    Promise.resolve()
      .then(() =>
        stripeLoader(
          payment.publishableKey,
          payment.connectedAccountId
            ? { stripeAccount: payment.connectedAccountId }
            : undefined
        )
      )
      .then(
        (loaded) => {
          if (active) {
            setStripe(loaded);
            setFailed(!loaded);
          }
        },
        () => {
          if (active) {
            setFailed(true);
          }
        }
      );
    return () => {
      active = false;
    };
  }, [payment, stripeLoader, revision]);
  if (failed) {
    return (
      <div>
        <p role="alert">Payment details could not load.</p>
        {resumePurchase ? null : (
          <button onClick={onBack} type="button">
            Back to item
          </button>
        )}
        <button
          onClick={() => {
            setFailed(false);
            setStripe(null);
            setRevision((value) => value + 1);
          }}
          type="button"
        >
          Try again
        </button>
      </div>
    );
  }
  if (!(stripe && appearance)) {
    return (
      <div>
        <p role="status">Loading secure payment details…</p>
        {resumePurchase ? null : (
          <button onClick={onBack} type="button">
            Back to item
          </button>
        )}
      </div>
    );
  }
  let paymentOptions: StripeElementsOptionsMode = {
    mode: "setup",
    setupFutureUsage: "off_session",
  };
  if (resumePurchase) {
    paymentOptions = {
      amount: resumePurchase.pricing.totalCents,
      currency: "usd",
      mode: "payment",
    };
  } else if (item.saleMode === "buy_now") {
    paymentOptions = {
      amount: getAuctionSelectionPricing(auction, item, selection).totalCents,
      currency: "usd",
      mode: "payment",
    };
  }
  const options: StripeElementsOptions = {
    ...paymentOptions,
    appearance,
    currency: "usd",
    paymentMethodTypes: ["card"],
    syncAddressCheckbox: "none",
  };
  return (
    <Elements options={options} stripe={stripe}>
      <AuctionCheckout {...props} />
    </Elements>
  );
}

function AuctionProgress({ buyNow }: { buyNow: boolean }) {
  return (
    <div className="lockerverse-auctions__progress">
      <span>
        {buyNow ? (
          <ShieldCheck aria-hidden="true" size={16} />
        ) : (
          <Gavel aria-hidden="true" size={16} />
        )}
        {buyNow ? "Choose quantity" : "Place bid"}
      </span>
      <span>
        <ShieldCheck aria-hidden="true" size={16} />
        {buyNow ? "Pay securely" : "Secure card"}
      </span>
      <span>
        <TimerReset aria-hidden="true" size={16} />
        {buyNow ? "Get confirmation" : "Wait for close"}
      </span>
    </div>
  );
}

function AuctionItemView(props: LockerverseAuctionItemProps) {
  const {
    auction: initialAuction,
    itemId,
    resumePurchase,
    className,
    theme = "dark",
    style,
  } = props;
  const appearanceRoot = useRef<HTMLElement | null>(null);
  const [auction, setAuction] = useState(initialAuction);
  const acceptedHost = useRef(initialAuction);
  const item =
    auction.items.find((candidate) => candidate.id === itemId) ??
    resumePurchase?.item;
  useAuctionBoundary(
    item?.startAt ?? auction.startAt,
    item?.endAt ?? auction.endAt
  );
  const [selection, setSelection] = useState<AuctionSelection | null>(() =>
    resumePurchase
      ? {
          amount: 0,
          includeServiceFee:
            resumePurchase.submission.includeServiceFee ?? false,
          quantity: resumePurchase.submission.quantity,
        }
      : null
  );
  const [interactionLocked, setInteractionLocked] = useState(
    Boolean(resumePurchase)
  );
  const viewMounted = useRef<boolean>(true);
  const hostCallbacks = useRef(props);
  hostCallbacks.current = props;
  const reportedLock = useRef<boolean | null>(null);
  const updateInteractionLock = useCallback((locked: boolean) => {
    // biome-ignore lint/suspicious/noUnnecessaryConditions: Effect cleanup changes this ref outside the callback.
    if (viewMounted.current) {
      setInteractionLocked(locked);
    }
    if (reportedLock.current !== locked) {
      reportedLock.current = locked;
      notifyHostCallback(hostCallbacks.current.onInteractionLockChange, locked);
    }
  }, []);
  const hasRecovery = Boolean(resumePurchase);
  useEffect(() => {
    viewMounted.current = true;
    updateInteractionLock(hasRecovery);
    return () => {
      viewMounted.current = false;
    };
  }, [hasRecovery, updateInteractionLock]);
  const [contact, setContact] = useState({ email: "", phone: "" });
  const [paying, setPaying] = useState(Boolean(resumePurchase));
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    if (
      !(paying || interactionLocked || resumePurchase) &&
      acceptedHost.current !== initialAuction
    ) {
      acceptedHost.current = initialAuction;
      setAuction(initialAuction);
    }
  }, [initialAuction, paying, interactionLocked, resumePurchase]);

  function review(current: LockerverseAuction, message: string) {
    updateInteractionLock(false);
    // The checkout just fetched this snapshot. Do not restore an older host
    // snapshot that arrived while payment was open; accept its next refresh.
    acceptedHost.current = hostCallbacks.current.auction;
    setAuction(current);
    setSelection(null);
    setPaying(false);
    setNotice(message);
  }
  function continueToPayment(next: AuctionSelection) {
    setNotice(null);
    setSelection(next);
    setPaying(true);
  }
  if (!item) {
    return (
      <div>
        <p role="alert">This auction item is no longer available.</p>
        {props.onBack ? (
          <button onClick={props.onBack} type="button">
            Back to auction items
          </button>
        ) : null}
      </div>
    );
  }
  return (
    <section
      className={["lockerverse-auctions", className].filter(Boolean).join(" ")}
      data-lockerverse-theme={theme}
      ref={appearanceRoot}
      style={{
        "--lockerverse-accent": auction.community?.color ?? "#6f8f6d",
        ...style,
      }}
    >
      {props.onBack && !interactionLocked ? (
        <button
          className="lockerverse-auctions__back"
          onClick={props.onBack}
          type="button"
        >
          <ChevronLeft aria-hidden="true" size={16} /> All items
        </button>
      ) : null}
      <div className="lockerverse-auctions__detail">
        <AuctionItemDetails auction={auction} item={item} />
        <div className="lockerverse-auctions__transaction">
          {getAuctionDisplayStatus(auction, item) === "active" ? (
            <AuctionProgress buyNow={item.saleMode === "buy_now"} />
          ) : null}
          {notice ? (
            <p className="lockerverse-auctions__notice" role="alert">
              {notice}
            </p>
          ) : null}
          {paying && selection ? (
            <AuctionPayment
              {...props}
              appearanceRoot={appearanceRoot}
              auction={auction}
              communityName={props.communityName ?? auction.community?.name}
              contact={contact}
              interactionLocked={interactionLocked}
              item={item}
              onBack={() => setPaying(false)}
              onContactChange={setContact}
              onIncludeServiceFeeChange={(includeServiceFee) =>
                setSelection((current) =>
                  current
                    ? {
                        ...current,
                        includeServiceFee:
                          auction.serviceFeeEnabled && includeServiceFee,
                      }
                    : current
                )
              }
              onInteractionLockChange={updateInteractionLock}
              onReview={review}
              selection={selection}
            />
          ) : (
            <AuctionItemSelection
              auction={auction}
              initialSelection={selection}
              item={item}
              onBack={props.onBack}
              onContinue={continueToPayment}
              onError={setNotice}
            />
          )}
          {props.onBack && !interactionLocked ? (
            <button
              className="lockerverse-auctions__footer-back"
              onClick={props.onBack}
              type="button"
            >
              Back to auction items <ArrowRight aria-hidden="true" size={16} />
            </button>
          ) : null}
        </div>
      </div>
    </section>
  );
}

export function LockerverseAuctionItem(props: LockerverseAuctionItemProps) {
  const identity = useRef({
    auctionId: props.auction.id,
    client: props.client,
    itemId: props.itemId,
  });
  if (
    identity.current.client !== props.client ||
    identity.current.auctionId !== props.auction.id ||
    identity.current.itemId !== props.itemId
  ) {
    throw new TypeError(
      "Auction item identity cannot change. Remount with a new React key."
    );
  }
  if (
    props.resumePurchase &&
    (props.resumePurchase.auctionId !== props.auction.id ||
      props.resumePurchase.itemId !== props.itemId ||
      props.resumePurchase.item.id !== props.itemId ||
      props.resumePurchase.item.auctionId !== props.auction.id)
  ) {
    throw new TypeError(
      "The purchase recovery record belongs to a different auction item."
    );
  }
  // Keep the view mounted when a refreshed host snapshot removes the item.
  // Its transaction snapshot remains authoritative until it is safe to leave.
  return <AuctionItemView {...props} />;
}
